/// <amd-module name="Repos/Tfvc/Views/ChangeDetails/ChangeDetailsViewService" />
import { DefaultDiscussionFilterOption, IArtifactDiscussionThreadService } from "Repos/Artifact/Discussion/ArtifactDiscussionThreadService";
import { IChangeWithDiscussionThread } from "Repos/Changes/Contracts";
import { getTfvcNavigationService } from "Repos/ChangesCommon/ChangesNavigationService";
import { IChange, IChangeCollection, IChangesViewService } from "Repos/ChangesCommon/Contracts";
import { Change, TfvcVersionDescriptor, TfvcVersionOption, TfvcVersionType, VersionControlChangeType } from "Repos/Common/Contracts/Git";
import { getIconNameForFile } from "Repos/Common/Util/File";
import { getTfvcVersionString, tfvcVersionStringToVersionDescriptor } from "Repos/Common/Util/Version";
import { IChangesTreeDiscussionIntegration, IDiffSummaryDiscussionIntegration } from "Repos/Discussion/Common/DiscussionContracts";
import { addCommentToFileItem, addCommentToRootItem } from "Repos/Discussion/Editor/Utils/CommentAction";
import { getTfvcRoutingService } from "Repos/Tfvc/Common/TfvcRoutingService";
import { getTfvcFilesService } from "Repos/Tfvc/Files/TfvcFilesService";
import * as Resources from "Repos/Tfvc/Views/ChangeDetails/Resources";
import { IObservableValue, IReadonlyObservableArray, IReadonlyObservableValue, ObservableCollection, ObservableValue } from "VSS/Core/Observable";
import { caseInsensitiveContains, EmptyGuidString, format } from "VSS/Core/Util/String";
import { HistoryBehavior, IPageStateHistoryService } from "VSS/Features/History";
import { PersonaDetailsProvider } from "VSS/Features/Identities/PersonaDetailsProvider";
import { IUnsavedChangesService } from "VSS/Features/PlatformUI/UnsavedChangesService";
import { IVssPageContext, IVssService, VssService } from "VSS/Platform/Context";
import { IdentityRef } from "VSS/Platform/Contracts/WebApi";
import { IVssContributionService } from "VSS/Platform/Contribution";
import { onClickFPS } from "VSS/Platform/FPS";
import { IHeaderCommandBarItem } from "VSSUI/HeaderCommandBar";
import { IMenuItem } from "VSSUI/Menu";
import { Filter, IFilterState } from "VSSUI/Utilities/Filter";
import { ITreeItem } from "VSSUI/Utilities/TreeItemProvider";
import { ITfvcChangeData, ITfvcChangeDetails, ITfvcChangeItem } from "./Contracts";
import { getTfvcChangesCollection } from "./Util";

export namespace FilesFilterKeys {
    export const keyword = "keyword";
    export const discussions = "discussions";
}

interface IPagedChangesParameters {
    maxChanges: number;
    repositoryId: string;
    skipCount: number;
    version: string;
}

interface IPagedChangesResult {
    results: Change<ITfvcChangeItem>[];
    moreResultsAvailable: boolean;
}

export interface IChangeDetailsViewService extends IChangesViewService, IVssService {
    /**
     * Get the path of all files changed.
     */
    getAllChangedPaths(): string[];

    /**
     * Gets the id of the change.
     */
    getChangeId(): string;

    /**
     * Gets the full comment of the change.
     */
    getChangeFullComment(): string;

    /**
     * Gets the owner of the change set.
     */
    getChangeOwner(): IdentityRef;

    /**
     * Gets all the changes in this change set.
     */
    getChanges(): IReadonlyObservableValue<IChangeCollection<TfvcVersionDescriptor>>;

    /**
     * Gets the shortened version of change if the name is too long.
     */
    getChangeShortComment(): string;

    /**
     * Gets the check-in date of the change.
     */
    getCheckinDate(): Date;

    /**
     * Gets the default text for change set.
     */
    getDefaultChangeText(): string;

    /**
     * Gets title for the browser page.
     */
    getDocumentTitle(): string;

    /**
     * Gets the filter applied.
     */
    getFilesFilter(): Filter;

    /**
     * Gets the menu items which appear in the header.
     */
    getHeaderMenuItems(smallScreen: boolean): IHeaderCommandBarItem[] | undefined;

    /**
     * Gets the menu items which for each item in tree.
     *
     */
    getItemMenuItems(
        item: IChange,
        treeDiffIntegration?: IChangesTreeDiscussionIntegration<ITreeItem<IChangeWithDiscussionThread>>,
        rootDiffIntegration?: IDiffSummaryDiscussionIntegration
    ): IReadonlyObservableArray<IMenuItem>;

    /**
     * Gets the persona provider.
     */
    getPersonaProvider(): PersonaDetailsProvider;

    /**
     * Gets the currently selected tab (Files or Details)
     */
    getSelectedTabId(): IReadonlyObservableValue<string>;

    /**
     * Gets the currently selected thread.
     */
    getSelectedThreadId(): IReadonlyObservableValue<number | undefined>;

    /**
     * Gets whether the change is a Changeset or a Shelveset.
     */
    getVersionType(): TfvcVersionType;

    /**
     * Sets the selected tab (Files or Details)
     *
     * @param tabId Newly selected tab
     */
    setSelectedTabId(tabId: string): void;
}

const dataProviderId = "ms.vss-code-web.tfvc-details-view-data-provider";
const moreChangesDataProviderId = "ms.vss-code-web.changelist-changes-data-provider";
const changesBatchSize = 1000;

export abstract class ChangeDetailsViewService extends VssService implements IChangeDetailsViewService {
    private filesFilter = new Filter();
    private hasMoreChanges: boolean;
    private rawChanges: Change<ITfvcChangeItem>[] = [];
    protected id: string;
    protected changes: IObservableValue<IChangeCollection<TfvcVersionDescriptor>>;
    protected checkinDate: Date;
    protected defaultText: string;
    protected fullComment: string;
    protected owner: IdentityRef;
    protected personaProvider: PersonaDetailsProvider;
    protected selectedTabId: ObservableValue<string>;
    protected selectedThreadId: ObservableValue<number | undefined>;
    protected shortComment: string;

    public _serviceStart(pageContext: IVssPageContext): void {
        super._serviceStart(pageContext);

        const contributionService = pageContext.getService<IVssContributionService>("IVssContributionService");
        const changesData = contributionService.getData<ITfvcChangeData<ITfvcChangeItem>>(dataProviderId);

        if (changesData && changesData.tfvcChangeDetails) {
            this.initialize(pageContext, changesData.tfvcChangeDetails);
        } else {
            throw new Error("Change not found");
        }

        this.filesFilter.setFilterItemState(FilesFilterKeys.discussions, { value: [DefaultDiscussionFilterOption.id] });
        this.filesFilter.setDefaultState(this.filesFilter.getState());
        this.filesFilter.subscribe(this.applyFilesFilter);

        this.pageContext.getService<IUnsavedChangesService>("IUnsavedChangesService").register({
            isDirty: () => this.getDiscussionService().hasUnsavedThreads(),
            getDirtyMessage: () => Resources.UnsavedCommentsMessage
        });
    }

    public getAllChangedPaths(): string[] {
        return this.rawChanges.map(c => c.item.serverItem || c.sourceServerItem || "");
    }

    public getFilesFilter(): Filter {
        return this.filesFilter;
    }

    public abstract getHeaderMenuItems(smallScreen: boolean): IHeaderCommandBarItem[] | undefined;

    public getItemMenuItems = (
        item: IChange,
        treeDiffIntegration?: IChangesTreeDiscussionIntegration<ITreeItem<IChangeWithDiscussionThread>>,
        rootDiffIntegration?: IDiffSummaryDiscussionIntegration
    ): IReadonlyObservableArray<IMenuItem> => {
        const { pageContext } = this;

        const filesService = getTfvcFilesService(pageContext);
        const tfvcRoutingService = getTfvcRoutingService(pageContext);
        const itemVersion = tfvcVersionStringToVersionDescriptor(item.commitId);
        const targetVersion = itemVersion.versionType === TfvcVersionType.Shelveset ? "T" : item.commitId;

        const menuItems: IMenuItem[] = [];
        menuItems.push({
            groupKey: "file-items",
            iconProps: { iconName: "Page" },
            id: "view",
            href: tfvcRoutingService.getFilesHubUrl(item.path, targetVersion),
            onActivate: (menuItem, event) => {
                return onClickFPS(pageContext, menuItem.href!, true, event);
            },
            text: Resources.ViewInFileExplorer
        });

        if (!item.isFolder) {
            menuItems.push({
                groupKey: "file-items",
                iconProps: { iconName: "History" },
                id: "view-history",
                href: tfvcRoutingService.getFilesHistoryUrl(item.path, targetVersion),
                onActivate: (menuItem, event) => {
                    return onClickFPS(pageContext, menuItem.href!, true, event);
                },
                text: Resources.ViewHistory
            });

            if (itemVersion.versionType !== TfvcVersionType.Shelveset) {
                menuItems.push({
                    groupKey: "file-items",
                    iconProps: { iconName: "BranchCompare" },
                    id: "compare-previous",
                    href: tfvcRoutingService.getFilesCompareUrl(item.path, item.commitId, "P" + item.commitId),
                    onActivate: (menuItem, event) => {
                        return onClickFPS(pageContext, menuItem.href!, true, event);
                    },
                    text: Resources.CompareToPreviousVersion
                });

                menuItems.push({
                    groupKey: "file-items",
                    iconProps: { iconName: "BranchCompare" },
                    id: "compare-latest",
                    href: tfvcRoutingService.getFilesCompareUrl(item.path, "T", item.commitId),
                    onActivate: (menuItem, event) => {
                        return onClickFPS(pageContext, menuItem.href!, true, event);
                    },
                    text: Resources.CompareToLatestVersion
                });
            }

            menuItems.push({
                groupKey: "file-actions",
                iconProps: { iconName: "Download" },
                id: "file-download",
                onActivate: () => {
                    const url = filesService.getFileDownloadUrl(item.path, itemVersion);
                    window.open(url, "_blank", "noopener");
                },
                text: Resources.Download
            });

            if (treeDiffIntegration) {
                menuItems.push(
                    addCommentToFileItem(treeDiffIntegration, item.path, () => {
                        const navigationService = getTfvcNavigationService(pageContext);
                        navigationService.setSelectedItem(item);
                    })
                );
            }
        }

        if (rootDiffIntegration && this.isRootItem(item)) {
            menuItems.push(
                addCommentToRootItem(rootDiffIntegration, format(Resources.AddComment, this.getChangeName()), true, () => {
                    const navigationService = getTfvcNavigationService(pageContext);
                    navigationService.setSelectedItem(item);
                })
            );
        }

        const collection = new ObservableCollection<IMenuItem>();
        collection.push(menuItems);
        return collection;
    };

    public getChangeId(): string {
        return this.id;
    }

    public getChangeFullComment(): string {
        return this.fullComment;
    }

    public getChangeOwner(): IdentityRef {
        return this.owner;
    }

    public getChanges(): IObservableValue<IChangeCollection<TfvcVersionDescriptor>> {
        return this.changes;
    }

    public getChangeShortComment(): string {
        return this.shortComment;
    }

    public getCheckinDate(): Date {
        return this.checkinDate;
    }

    public getCompareHeaderMenuItems(item: IChange, rootDiffIntegration?: IDiffSummaryDiscussionIntegration): IMenuItem[] | undefined {
        if (rootDiffIntegration && this.isRootItem(item)) {
            return [addCommentToRootItem(rootDiffIntegration, format(Resources.AddComment, this.getChangeName()), false)];
        }
    }

    public getDefaultChangeText(): string {
        return this.defaultText;
    }

    public abstract getDocumentTitle(): string;

    public getPersonaProvider(): PersonaDetailsProvider {
        return this.personaProvider;
    }

    public getSelectedTabId(): IReadonlyObservableValue<string> {
        return this.selectedTabId;
    }

    public getSelectedThreadId(): IReadonlyObservableValue<number | undefined> {
        return this.selectedThreadId;
    }

    public abstract getVersionType(): TfvcVersionType;

    public isRootItem(item: IChange): boolean {
        const rootItem = this.changes.value.rootTreeItem.data;
        return item === rootItem;
    }

    public setSelectedTabId(tabId: string): void {
        this.selectedTabId.value = tabId;
    }

    protected getItem(path: string = ""): IChange {
        const { changes } = this;

        if (path === "" && changes.value.rootTreeItem) {
            return changes.value.rootTreeItem.data;
        }

        let item = changes.value.changesByPath[path];
        if (!item) {
            // Look for the item ignoring casing
            item = changes.value.changesByPath[path.toLocaleLowerCase()];
            if (!item) {
                // Look for folder
                const treeItem = changes.value.treeItemsByPath[path];
                if (treeItem) {
                    item = treeItem.data;
                }

                if (!item) {
                    // Item does not exist. Create a entry for the invalid item
                    const invalidItem = {
                        iconProps: { iconName: getIconNameForFile(path) },
                        isFolder: false,
                        isInvalid: true,
                        isRepo: false,
                        folder: path,
                        path: path,
                        name: path,
                        commitId: this.getChangeId(),
                        changeType: VersionControlChangeType.None,
                        objectId: undefined
                    };
                    item = invalidItem;
                }
            }
        }
        return item;
    }

    protected abstract getChangeName(): string;
    protected abstract getDiscussionService(): IArtifactDiscussionThreadService;
    protected abstract initializeFields(changesData: ITfvcChangeDetails<ITfvcChangeItem>): void;

    private applyFilesFilter = (filterState: IFilterState, action: string) => {
        if (action === "filter-applied") {
            if (filterState[FilesFilterKeys.discussions] && filterState[FilesFilterKeys.discussions]!.value.length) {
                this.getDiscussionService().setFilter(filterState[FilesFilterKeys.discussions]!.value[0]);
            }

            if (FilesFilterKeys.keyword in filterState) {
                this.changes.value = this.processRawChanges();
            }
        }
    };

    private initialize(pageContext: IVssPageContext, changesData: ITfvcChangeDetails<ITfvcChangeItem>): void {
        const { changes } = changesData;

        const pageStateHistoryService = pageContext.getService<IPageStateHistoryService>("IPageStateHistoryService");
        this.personaProvider = new PersonaDetailsProvider(pageContext);

        this.initializeFields(changesData);

        const navigationService = getTfvcNavigationService(pageContext);

        this.rawChanges = changes || [];
        this.hasMoreChanges = !changesData.allChangesIncluded;
        this.changes = new ObservableValue(this.processRawChanges());
        navigationService.setChanges(this.changes);
        const rootItem = this.changes.value.rootTreeItem ? this.changes.value.rootTreeItem.data : undefined;

        const historyState = pageStateHistoryService.getProjectedState();
        const path = historyState["path"];

        this.selectedThreadId = new ObservableValue(parseInt(historyState["discussionId"]) || undefined);

        navigationService.setSelectedItem(this.getItem(path));

        const selectedItem: IReadonlyObservableValue<IChange> = navigationService.getSelectedItem();

        selectedItem.subscribe((value: IChange): void => {

            const change: IChangeWithDiscussionThread = value as IChangeWithDiscussionThread;
            if (change.thread) {
                if (this.selectedThreadId.value !== change.thread.id) {
                    this.selectedThreadId.value = change.thread.id;
                }
            } else {
                if (this.selectedThreadId.value) {
                    this.selectedThreadId.value = undefined;
                }
            }
        });

        pageStateHistoryService.registerObservable<IChange>(
            navigationService.getSelectedItem(),
            "path",
            HistoryBehavior.newEntry,
            item => (rootItem && item.path === rootItem.path ? "" : item.path),
            path => {
                // Selected item path changed from the URL. Ensure that
                // we fully process the change by calling setSelectedItem.
                let item = this.getItem(path);
                if (item.isInvalid) {
                    item = this.getItem();
                }
                navigationService.setSelectedItem(item);
                return item;
            }
        );

        this.selectedTabId = new ObservableValue(historyState["tab"] || "files");

        pageStateHistoryService.registerObservable<string>(
            this.selectedTabId,
            "tab",
            HistoryBehavior.newEntry,
            selectedTabId => (selectedTabId === "files" ? "" : selectedTabId),
            queryParam => queryParam || "files"
        );

        pageStateHistoryService.registerObservable<number | undefined>(
            this.selectedThreadId,
            "discussionId",
            HistoryBehavior.newEntry,
            id => (id ? id.toFixed() : ""),
            queryParam => parseInt(queryParam) || undefined
        );
    }

    private processRawChanges(): IChangeCollection<TfvcVersionDescriptor> {
        const keywordFilterItemValue = this.filesFilter.getFilterItemValue<string>(FilesFilterKeys.keyword);
        const filteredChanges = keywordFilterItemValue
            ? this.rawChanges.filter(c => caseInsensitiveContains(c.item.serverItem || c.sourceServerItem || "", keywordFilterItemValue))
            : this.rawChanges;

        return getTfvcChangesCollection(
            filteredChanges,
            this.getChangeId(),
            this.getVersionType(),
            this.hasMoreChanges
                ? () => {
                      this.fetchChanges(this.rawChanges.length);
                  }
                : undefined
        );
    }

    private async fetchChanges(skipCount: number): Promise<void> {
        const contributionService = this.pageContext.getService<IVssContributionService>("IVssContributionService");
        contributionService
            .getDataAsync<IPagedChangesResult>(moreChangesDataProviderId, {
                version: getTfvcVersionString({
                    version: this.getChangeId(),
                    versionOption: TfvcVersionOption.None,
                    versionType: this.getVersionType()
                }),
                maxChanges: skipCount + changesBatchSize,
                repositoryId: EmptyGuidString,
                skipCount
            } as IPagedChangesParameters)
            .then(data => {
                if (data) {
                    this.rawChanges = [...this.rawChanges, ...(data.results || [])];
                    this.hasMoreChanges = data.moreResultsAvailable;
                    this.changes.value = this.processRawChanges();
                }
            });
    }
}
